import React from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import Dialog from 'components/dialog'
import GolfCourseIcon from 'components/material-symbol/icons/GolfCourse'
import ChatBubbleIcon from 'components/material-symbol/icons/ChatBubble'
import TrendingUpIcon from 'components/material-symbol/icons/TrendingUp'
import VisibilityIcon from 'components/material-symbol/icons/Visibility'
import { redirectToLogin, redirectToSignup } from 'lib/auth'
import { CARD_BORDER, MUTED_TEXT, NAVY_TINT, SERIF } from './styles'

// "Create a free account" prompt for the Virtual Coaching checkout. It is not
// the shared `components/account-benefits-modal`: that one is used by the
// private lesson booking flows and keeps its own copy and look. Tokens match
// the rest of the golfer pages (imported from ./styles).

// Only claim what golfers can do today: see their requests and the coach's
// reply under /club/virtual-coaching, and message coaches at /club/messages.
export const BENEFITS = [
  {
    Icon: VisibilityIcon,
    title: 'Your swing, expert eyes.',
    description: 'Get personalized analysis from a PGA Coach.',
  },
  {
    Icon: TrendingUpIcon,
    title: 'Progress worth tracking.',
    description: "Revisit your feedback and see how far you've come.",
  },
  {
    Icon: ChatBubbleIcon,
    title: 'Your coach, in your corner.',
    description: 'Message your coach any time.',
  },
]

const useStyles = makeStyles(theme => ({
  // The base theme sets the paper radius as an inline style, so only
  // `!important` can override it.
  paper: {
    borderRadius: '24px !important',
  },
  paperFullScreen: {
    borderRadius: '0 !important',
  },
  root: {
    padding: 0,
    '&:first-child': { paddingTop: 0 },
  },
  inner: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: 560,
    margin: '0 auto',
  },
  header: {
    padding: theme.spacing(6, 4, 4),
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(8, 3, 3),
    },
  },
  flag: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2.5),
  },
  headline: {
    margin: 0,
    fontFamily: SERIF,
    fontSize: 32,
    textWrap: 'balance',
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('xs')]: { fontSize: 30 },
  },
  subheading: {
    margin: theme.spacing(1.5, 0, 0),
    fontSize: 16,
    lineHeight: 1.6,
    textWrap: 'pretty',
    color: MUTED_TEXT,
  },
  body: {
    padding: theme.spacing(1, 4, 5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 3, 4),
    },
  },
  benefits: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  benefit: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
  },
  tile: {
    flexShrink: 0,
    width: 48,
    height: 48,
    borderRadius: 14,
    backgroundColor: NAVY_TINT,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { fontSize: 24 },
  },
  benefitTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  benefitDescription: {
    margin: theme.spacing(0.25, 0, 0),
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    color: MUTED_TEXT,
  },
  cta: {
    marginTop: theme.spacing(4),
    height: 52,
    fontSize: 16,
  },
  signInRow: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.5,
    color: MUTED_TEXT,
  },
  signInLink: {
    background: 'none',
    border: 0,
    padding: theme.spacing(0.5),
    font: 'inherit',
    fontWeight: 700,
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    cursor: 'pointer',
    '&:hover': { textDecorationThickness: 2 },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 4,
    },
  },
}))

const SwingAnalysisAccountModal = ({ isOpen, setIsOpen }) => {
  const classes = useStyles()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('xs'))

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      isOpen={isOpen}
      showCloseButton
      fullScreen={isMobile}
      classes={{ paper: isMobile ? classes.paperFullScreen : classes.paper }}
      aria-labelledby="swing-analysis-account-title"
      onClose={() => setIsOpen(false)}
    >
      {() => (
        <DialogContent className={classes.root}>
          <div className={classes.inner}>
            <div className={classes.header}>
              <div className={classes.flag} aria-hidden="true">
                <CircularHighlightedIcon
                  icon={GolfCourseIcon}
                  highlightColor={NAVY_TINT}
                />
              </div>
              <Typography
                component="h2"
                id="swing-analysis-account-title"
                className={classes.headline}
              >
                Your better swing starts here.
              </Typography>
              <Typography className={classes.subheading}>
                Great golf doesn&apos;t happen by accident. Create your free
                MyPGA account to get expert feedback, stay connected with your
                coach, and keep improving.
              </Typography>
            </div>
            <div className={classes.body}>
              <ul className={classes.benefits}>
                {BENEFITS.map(({ Icon, title, description }) => (
                  <li className={classes.benefit} key={title}>
                    <span className={classes.tile} aria-hidden="true">
                      <Icon />
                    </span>
                    <div>
                      <Typography className={classes.benefitTitle}>
                        {title}
                      </Typography>
                      <Typography className={classes.benefitDescription}>
                        {description}
                      </Typography>
                    </div>
                  </li>
                ))}
              </ul>
              <Button
                className={classes.cta}
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => redirectToSignup('consumer')}
              >
                Create Free Account
              </Button>
              <Typography className={classes.signInRow}>
                Already have an account?{' '}
                <button
                  type="button"
                  className={classes.signInLink}
                  onClick={() => redirectToLogin('consumer')}
                >
                  Sign in
                </button>
              </Typography>
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}

SwingAnalysisAccountModal.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func.isRequired,
}

export default SwingAnalysisAccountModal
